import React, {useEffect, useRef, useState} from 'react'
import {useDebounce} from "use-debounce";
import {NavLink} from "react-router-dom";
import {searchAddressAPI} from "../../network/publicCalls/GETSearchAddress";
import {searchLocationAPI} from "../../network/publicCalls/GETSearchLocation";
import {searchAccessoriesLocationAPI} from "../../network/publicCalls/GETSearchAccessoriesLocation";

const SearchField = ({vehicleType, selectedRentTypes = []}) => {
    const PRIORITY_SUBSIDIARY_ID = "9"

    const [searchResultReady, setSearchResultReady] = useState(false)
    const [query, setQuery] = useState("")
    const [queryToSearch] = useDebounce(query, 400)
    const [isLoading, setIsLoading] = useState(false)
    const searchRef = useRef(null)
    const [locationList, setLocationList] = useState([])

    const normalizeDistance = (value) => {
        if (value === null || value === undefined) return null
        if (typeof value === "number") return value
        if (typeof value === "string") {
            const parsed = Number(value.replace(",", "."))
            return Number.isFinite(parsed) ? parsed : null
        }
        return null
    }

    const normalizeLocations = (locations) => {
        const list = Array.isArray(locations)
            ? locations
            : Array.isArray(locations?.items)
                ? locations.items
                : Array.isArray(locations?.data)
                    ? locations.data
                    : []

        return list
            .map((location, index) => ({
                id: location?.id ?? index + 1,
                name: location?.name || location?.title || "",
                distance_km: normalizeDistance(
                    location?.distance_km ?? location?.distanceKm ?? location?.distance
                ),
                subsidiary_id: location?.subsidiary?.id ? String(location.subsidiary.id) : null
            }))
            .sort((a, b) => {
                const aPriority = a.subsidiary_id === PRIORITY_SUBSIDIARY_ID ? 0 : 1
                const bPriority = b.subsidiary_id === PRIORITY_SUBSIDIARY_ID ? 0 : 1

                if (aPriority !== bPriority) return aPriority - bPriority

                const aDistance = Number.isFinite(a.distance_km) ? a.distance_km : Number.POSITIVE_INFINITY
                const bDistance = Number.isFinite(b.distance_km) ? b.distance_km : Number.POSITIVE_INFINITY

                if (aDistance !== bDistance) return aDistance - bDistance

                return String(a.name).localeCompare(String(b.name))
            })
    }

    const onQueryInput = (e) => {
        setQuery(e.target.value)
    }

    useEffect(() => {
        if (!queryToSearch) {
            setSearchResultReady(false)
            setLocationList([])
            return
        }
        triggerSearchAddress(queryToSearch, vehicleType.slug, selectedRentTypes)
    }, [queryToSearch, vehicleType, selectedRentTypes]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!searchRef.current) return
            if (!searchRef.current.contains(event.target)) {
                setSearchResultReady(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const triggerSearchAddress = async (address, vehicleType, rentTypes = []) => {
        setIsLoading(true)
        try {
            const addressResult = await searchAddressAPI({
                address: address
            })
            if (addressResult.location) {
                const {lat, lng} = addressResult.location
                if (lat && lng) {
                    const locations = vehicleType === "accessoires"
                        ? await searchAccessoriesLocationAPI({lat, lng})
                        : await searchLocationAPI({lat, lng, vehicleType, rentTypes})
                    const normalizedLocations = normalizeLocations(locations)
                    setLocationList(normalizedLocations)
                    setSearchResultReady(Array.isArray(normalizedLocations) && normalizedLocations.length > 0)
                } else {
                    setLocationList([])
                    setSearchResultReady(false)
                }
            } else {
                setLocationList([])
                setSearchResultReady(false)
            }
        } catch (e) {
            console.log(e)
            setLocationList([])
            setSearchResultReady(false)
        } finally {
            setIsLoading(false)
        }
    }

    const onFocusHandler = () => {

        if (locationList.length > 0) {
            setSearchResultReady(true)
        } else {
            setSearchResultReady(false)
        }

        if (queryToSearch.length === 0) {
            setSearchResultReady(false)
        }
    }

    const onFormSubmit = async (e) => {
        e.preventDefault()
        if (queryToSearch) {
            await triggerSearchAddress(queryToSearch, vehicleType.slug, selectedRentTypes)
        }
    }

    const getDistanceLabel = (distanceKmValue) => {
        const distance = normalizeDistance(distanceKmValue);
        if (!Number.isFinite(distance)) return null;
        return `${Math.round(distance)} KM`;
    };

    const resultsTitle = vehicleType.slug === "accessoires"
        ? "Beschikbare locaties voor accessoires"
        : "Vestigingen en Pick up Points";

    const headingTitle = vehicleType.slug === "accessoires"
        ? "Waar wil je accessoires huren of kopen?"
        : "Waar wil je een voertuig huren?";

    const inputPlaceholder = vehicleType.slug === "accessoires"
        ? "Typ je postcode of adres"
        : "Typ je plaats of postcode";

    return (
        <div className="row align-items-center front-search-form-row">
            <div className="col-md-6">
                <h1 className="red-title-1">{headingTitle}</h1>
            </div>
            <div className="col-md-6">
                <form onSubmit={onFormSubmit} action="#" className="front-search-form" ref={searchRef}>
                    <div style={{width: '100%', position: 'relative'}}>
                        <input
                            onFocus={onFocusHandler}
                            onChange={onQueryInput} value={query} type="text"
                               placeholder={inputPlaceholder}/>
                        {isLoading && <span className="front-search-form-loading" aria-hidden="true"></span>}
                    </div>
                    <button type="submit">Zoeken <i className="bi bi-arrow-right-short"></i></button>

                    {
                        searchResultReady && (
                            <div className="front-search-form-results">
                                <p>{resultsTitle}</p>
                                <ul>
                                    {
                                        locationList.map(({id, name, distance_km}) => {
                                            const distanceLabel = getDistanceLabel(distance_km);
                                            return (
                                                <li key={id}>
                                                    <NavLink
                                                        to={vehicleType.slug === "accessoires"
                                                            ? `/accessoires?locationId=${encodeURIComponent(String(id))}`
                                                            : `/locations/${id}/${vehicleType.slug}`}
                                                    >
                                                        {name}
                                                        {distanceLabel && (
                                                            <span>{distanceLabel}</span>
                                                        )}
                                                    </NavLink>
                                                </li>
                                            )
                                        })
                                    }
                                </ul>
                            </div>
                        )
                    }

                </form>
            </div>
        </div>
    )
}

export default SearchField
